import { create } from 'zustand';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  created_at: string;
}

export interface Conversation {
  id: string;
  title: string | null;
  context_type: 'general' | 'learning_path' | 'content_item' | 'welcome';
  context_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  messages?: ChatMessage[];
}

interface ChatState {
  // State
  isOpen: boolean;
  conversations: Conversation[];
  activeConversationId: string | null;
  unreadCount: number;
  isLoading: boolean;
  currentMessage: string;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  setActiveConversation: (conversationId: string | null) => void;
  setConversations: (conversations: Conversation[]) => void;
  addMessage: (conversationId: string, message: ChatMessage) => void;
  setCurrentMessage: (message: string) => void;
  setLoading: (loading: boolean) => void;
  incrementUnread: () => void;
  resetUnread: () => void;
  reset: () => void;
}

export const useChatStore = create<ChatState>()((set, get) => ({
      // Initial state
      isOpen: false,
      conversations: [],
      activeConversationId: null,
      unreadCount: 0,
      isLoading: false,
      currentMessage: '',

      // Actions
      openChat: () => set({ isOpen: true }),
      
      closeChat: () => set({ isOpen: false }),
      
      toggleChat: () => set((state) => ({ isOpen: !state.isOpen })),
      
      setActiveConversation: (conversationId) => {
        console.log('🎯 setActiveConversation called:', conversationId);
        set({ 
          activeConversationId: conversationId,
          unreadCount: 0 // Reset unread when opening conversation
        });
        console.log('✅ activeConversationId now:', get().activeConversationId);
      },
      
      setConversations: (conversations) => {
        console.log('📝 setConversations called with', conversations.length, 'conversations');
        conversations.forEach(c => console.log('  - Conv:', c.id, 'messages:', c.messages?.length || 0));
        set({ conversations });
        console.log('✅ Store updated, conversations now:', get().conversations.length);
      },
      
      addMessage: (conversationId, message) => {
        const conversations = get().conversations;
        console.log('💾 addMessage called:', {
          conversationId,
          messageRole: message.role,
          messageContent: message.content.substring(0, 30),
          conversationsCount: conversations.length,
          conversationIds: conversations.map(c => c.id)
        });
        
        const updatedConversations = conversations.map(conv => {
          if (conv.id === conversationId) {
            console.log('✅ Found matching conversation, adding message. Current messages:', conv.messages?.length || 0);
            return {
              ...conv,
              messages: [...(conv.messages || []), message],
              updated_at: new Date().toISOString()
            };
          }
          return conv;
        });
        
        // Check if conversation was found
        const conversationFound = updatedConversations.some(c => c.id === conversationId);
        if (!conversationFound) {
          console.error('❌ CONVERSATION NOT FOUND IN ARRAY!', conversationId);
        }
        
        set({ conversations: updatedConversations });
        console.log('💾 Store updated. New conversations:', get().conversations.length);
        
        // Increment unread if message is from assistant and chat is closed
        // But not for context messages (messages with ID starting with 'context-')
        if (message.role === 'assistant' && !get().isOpen && !message.id.startsWith('context-')) {
          set((state) => ({ unreadCount: state.unreadCount + 1 }));
        }
      },
      
      setCurrentMessage: (message) => set({ currentMessage: message }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      incrementUnread: () => set((state) => ({ unreadCount: state.unreadCount + 1 })),
      
      resetUnread: () => set({ unreadCount: 0 }),
      
      reset: () => {
        console.log('🔄 Resetting chat store...');
        set({
          isOpen: false,
          conversations: [],
          activeConversationId: null,
          unreadCount: 0,
          isLoading: false,
          currentMessage: ''
        });
      }
    }));

